import { Fragment, useEffect, useState } from "react";
import { Card, CardBody, CardHeader, CardFooter } from "@/components/bootstrap/TemporaryReactstrapCompatibilityLayer";
import Button from "../layout/Button";
import { TemplateUpload } from "./TemplateUpload";
import Loading from "../ui-elements/Loading";
import ConfirmDeleteModal from "../management/seedTypes/ConfirmDeleteModal";

const TemplateEditorCard = ({
  template,
  editOn,
  deleteHandler,
  updateHandler,
}) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);

  const initialState = {
    id: {
      value: template.id,
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
    name: {
      value: template.name ?? "",
      touched: false,
      validate: (text) => text?.length >= 3,
      isValid: !!template?.name,
      placeholder: "Template name ...",
    },
    description: {
      value: template.description ?? "",
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: "Template description ...",
    },
    template: {
      value: template?.template ?? "",
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
  };

  const [edit, setEdit] = useState(editOn);
  const [templateState, setTemplateState] = useState(initialState);

  useEffect(() => {
    if (template) {
      const templateStateInit = Object.entries(template).reduce(
        (acc, [key, value]) => {
          if (!(key in templateState)) return acc;
          return {
            ...acc,
            [key]: { ...acc[key], value, isValid: acc[key]?.validate(value) },
          };
        },
        initialState
      );
      setTemplateState(templateStateInit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const onChange = (event) => {
    event.persist();
    const key = event.currentTarget.name;
    const value = event.currentTarget.value;
    setTemplateState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
        isValid: prev[key].validate(value),
        touched: true,
      },
    }));
  };

  const activateEditState = () => setEdit(true);

  const reset = () => {
    setTemplateState(initialState);
    setEdit(false);
  };

  const createRequestBody = () => {
    const updatedTemplateProperties = Object.entries(templateState).reduce(
      (acc, [key, valueObject]) => ({ ...acc, [key]: valueObject.value }),
      {}
    );
    return { ...template, ...updatedTemplateProperties };
  };

  const handleUpdate = () => {
    if (blockSaveButton()) return;
    setShowConfirmUpdate(true);
  };

  const confirmUpdate = async () => {
    setLoading(true);
    try {
      const updatedTemplate = createRequestBody();
      await updateHandler(updatedTemplate);
      setEdit(false);
    } finally {
      setLoading(false);
      setShowConfirmUpdate(false);
    }
  };

  const blockSaveButton = () => {
    const updatedTemplate = createRequestBody();
    if (
      updatedTemplate?.name === template?.name &&
      updatedTemplate.description === template?.description &&
      updatedTemplate.fileName === template?.fileName &&
      updatedTemplate?.template === template?.template
    ) {
      return true;
    }
    if (!areAllValuesValid()) return true;
    return false;
  };

  const areAllValuesValid = () => {
    for (const key in templateState) {
      if (!Object.prototype.hasOwnProperty.call(templateState, key)) continue;
      const field = templateState[key];
      if (!field.validate(field.value)) return false;
    }
    return true;
  };

  const handleDelete = () => setShowConfirmDelete(true);

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await deleteHandler(template.id);
    } finally {
      setLoading(false);
      setShowConfirmDelete(false);
    }
  };

  const templateChangeHandler = (data, fileName) => {
    setTemplateState((prev) => ({
      ...prev,
      fileName: {
        value: fileName,
        validate: () => true,
        isValid: true,
        touched: true,
      },
      template: {
        ...initialState.template,
        value: data,
        isValid: true,
        touched: true,
      },
    }));
  };

  const templateResetHandler = () => {
    setTemplateState((prev) => ({
      ...prev,
      template: {
        ...template,
        value: null,
        isValid: true,
        touched: true,
      },
    }));
  };

  return loading ? (
    <Loading fullScreen={true} />
  ) : (
    <>
      <ConfirmDeleteModal
        show={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        header="DELETE TEMPLATE"
        message={
          <>
            This will permanently delete the template and its file reference.
            <br />
            This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <ConfirmDeleteModal
        show={showConfirmUpdate}
        onCancel={() => setShowConfirmUpdate(false)}
        onConfirm={confirmUpdate}
        header="Save changes?"
        message={
          <>
            Do you want to save your changes to this template?
            <br />
            Existing uses of this template may reflect the updated
            name/description/file.
          </>
        }
        confirmLabel="Save"
        cancelLabel="Cancel"
      />

      <Card className="seed-card">
        <CardHeader>
          <Fragment>
            <input
              className={
                templateState.name.isValid || !templateState.name.touched
                  ? "h4-input seed-property-input-field header-input"
                  : "h4-input seed-property-input-field header-input invalid-field"
              }
              type="text"
              minLength="3"
              value={templateState.name.value}
              onChange={onChange}
              name="name"
              id="name"
              placeholder={templateState.name.placeholder}
              disabled={edit === false}
            />
            {!templateState.name.isValid && templateState.name.touched && (
              <p className="template-name-error-color">
                Name must have 3 characters or more.
              </p>
            )}
          </Fragment>
        </CardHeader>

        <CardBody className="seed-card-body">
          <textarea
            rows="3"
            name="description"
            className={
              templateState.description.isValid ||
              !templateState.description.touched
                ? "seed-property-input-field italic header-input"
                : "seed-property-input-field italic header-input invalid-field"
            }
            minLength="3"
            value={templateState.description.value}
            placeholder={
              edit === false
                ? templateState.description.value
                : templateState.description.placeholder
            }
            onChange={onChange}
            disabled={edit === false}
          />
          <br />
          {edit ? (
            <TemplateUpload
              changeHandler={templateChangeHandler}
              resetHandler={templateResetHandler}
              labelText={`Current template file: ${template.fileName}\nClick to replace with a new file.`}
              originalReuploadLabelText={`New template file: <fileName>\nClick to replace with a new file.`}
              originalTemplate={template}
              updatedTemplate={templateState}
              textSize="0.8em"
            />
          ) : (
            <CardFooter>
              <Fragment>
                <div>
                  <p>
                    <strong>{template.fileName}</strong>
                  </p>
                </div>
              </Fragment>
            </CardFooter>
          )}
        </CardBody>

        <div className="card-button seed-card-footer">
          {edit ? (
            <Fragment>
              <Button
                className="seed-card-btn"
                label="Cancel"
                clickHandler={reset}
              />
              <Button
                className="seed-card-btn"
                label="Save"
                clickHandler={handleUpdate}
                disabled={blockSaveButton()}
              />
            </Fragment>
          ) : (
            <Fragment>
              <Button
                className="seed-card-btn"
                label={"Delete"}
                clickHandler={handleDelete}
              />
              <Button
                className="seed-card-btn"
                label={"Edit"}
                clickHandler={activateEditState}
              />
            </Fragment>
          )}
        </div>
      </Card>
    </>
  );
};

export default TemplateEditorCard;
